<template>
    <div
    class="products-content-wrapper prevent-auto-margins"
    v-touch:swipe.left="swipeLeft"
    v-touch:swipe.right="swipeRight"
    :class="[variant, {
        'mobile': getMobileAppState,
        'apply-mobile-header-product-slider': applyMobileHeaderProductSlider
    }]">
        <!-- *********************************************
        *              Mobile logic wrapper              *
        ********************************************** -->
        <!-- Image Slider -->
        <slider-image
        v-if="getMobileAppState && applyMobileHeaderProductSlider && hasCarrocelSlider"
        hasExternalSwipe
        :selectedIndex="mobileSelectedIndex"
        :sliderList="productsList"
        @updateSelectedIndex="mobileSelectedIndex = $event"/>
        <!-- Generic Slider -->
        <slider-generic
        v-if="getMobileAppState && applyMobileHeaderProductSlider && !hasCarrocelSlider"
        hasExternalSwipe
        :selectedIndex="mobileSelectedIndex"
        :sliderList="productsList"
        @updateSelectedIndex="mobileSelectedIndex = $event">
            <div
            v-for="(item, index) in productsList"
            class="item-element"
            :key="`itemList${index}`"
            :class="{'only-one-card': productsList.length === 1}">
                <div class="title">{{item.title}}</div>
                <div class="short-description">{{item.shortDescription}}</div>
                <div v-if="creditProduct && !item.shortDescription" class="item-description">{{item.description}}</div>
            </div>
        </slider-generic>
        <!-- Product info -->
        <transition name="fade" mode="out-in">
            <div v-if="getMobileAppState && applyMobileHeaderProductSlider" class="mobile-transition-helper" :key="mobileSelectedIndex">
                <!-- Mobile Logic Wrapper -->
                <card-product-selector-product
                applyMobileHeaderProductSlider
                :variant="variant"
                :invertTitleAndDescription="invertTitleAndDescription"
                :params="productsList[mobileSelectedIndex]"
                :creditProduct="creditProduct"
                @clicked="emit('productClicked', $event)"
                @rightTopButtonClicked="emit('rightTopButtonClicked', productsList[mobileSelectedIndex])"
                @moreOptionsClicked="emit('moreOptionsClicked', { optionClicked: $event, product: productsList[mobileSelectedIndex] })"/>
            </div>
        </transition>
        <!-- *********************************************
        *                Web logic wrapper               *
        ********************************************** -->
        <div v-if="!getMobileAppState || !applyMobileHeaderProductSlider" class="web-logic-wrapper">
            <!-- Product Selector -->
            <card-product-selector-product
            v-for="(product, index) in productsList"
            :variant="variant"
            :key="`productList${index}`"
            :invertTitleAndDescription="invertTitleAndDescription"
            :params="product"
            @clicked="emit('productClicked', $event)"
            @rightTopButtonClicked="emit('rightTopButtonClicked', product)"
            @moreOptionsClicked="emit('moreOptionsClicked', { optionClicked: $event, product: product })"/>
        </div>
    </div>
</template>

<script setup>
import SliderImage from '@/components/generic/sliders/Image'
import SliderGeneric from '@/components/generic/sliders/Generic'
import CardProductSelectorProduct from '@/components/generic/cards/productSelector/Product'

/* Vue & Aux */
import { ref, computed } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

const emit = defineEmits(['productClicked', 'moreOptionsClicked', 'rightTopButtonClicked', 'updateSelectedProductIndex'])

/* Props
@applyMobileHeaderProductSlider - prop to force product header slider on mobile
@variant {String} - variant for product [without-box, with-box(default)]
@invertTitleAndDescription {Boolean} prop to invert order of title and description
@creditProduct {Boolean} prop to show product description on item card
@productsList { Array } - in which object with card info, needs to have:
	- title { String }
	- description { String }
    - disclaimer
    - hasPlafond { Boolean }
    - infoSheetUrl { String } - href to download infoSheet
	- infoSheetUrlBtnText { String } - text to display on infoSheet button text
    - infoSheetCustomSuccessMessage { String } - custom message for success toast
    - secondBtnText { String } - text to display on second button (second)
    - secondBtnClicked { Function } - function to aplly when the secound button is clicked
    - textBlocks { Array } - each item is an Object
        - label { String }
        - content { String }
        - mask { Object }
*/
const props = defineProps({
    applyMobileHeaderProductSlider: Boolean,
    variant: { type: String, default: 'with-box' },
    invertTitleAndDescription: Boolean,
    creditProduct: Boolean,
    productsList: Array
})

const { getMobileAppState } = mapGetters()

const mobileSelectedIndex = ref(0)

const hasCarrocelSlider = computed(() => {
    return props.productsList.find(obj => obj.imageUrl)
})

const swipeLeft = () => {
    if (mobileSelectedIndex.value === (props.productsList.length - 1)) return
    mobileSelectedIndex.value++
    emit('updateSelectedProductIndex', mobileSelectedIndex.value)
}

const swipeRight = () => {
    if (mobileSelectedIndex.value === 0) return
    mobileSelectedIndex.value--
    emit('updateSelectedProductIndex', mobileSelectedIndex.value)
}

emit('updateSelectedProductIndex', mobileSelectedIndex.value)
</script>

<style lang="scss" scoped>
.products-content-wrapper {
    @include commonEnteringAnimation;
    width: 100%;

    display: flex;
    flex-direction: column;
    gap: 16px;

    border-radius: 16px;

    .web-logic-wrapper {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    &:not(.apply-mobile-header-product-slider) {
        background-color: var(--secondary-light-4);

        &.without-box {
            padding: 0 32px;
        }

        &.mobile {
            padding-top: 2px;
        }
    }

    &.apply-mobile-header-product-slider {
        padding-bottom: 80px;
    }

    .item-element {
        width: calc(100% - 16px);
        height: 118px;
        padding: 24px 16px 16px 16px;

        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        gap: 8px;

        border-radius: 8px;
        background-color: var(--secondary-light-3);

        &.only-one-card {
            width: 100%;
        }

        .title {
            @include H3-Regular;
            color: var(--primary);

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .short-description {
            @include H7-Regular;
            color: var(--secondary-dark-1);
            line-height: 16px;

            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3; /* number of lines to show */
        }

        .item-description {
            @include H7-Regular;
            color: var(--secondary-dark-1);
            line-height: 16px;

            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3; /* number of lines to show */
        }
    }

    .mobile-transition-helper, .web-logic-wrapper {
        width: 100%;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.25s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
}
</style>
